import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Table,
  Card,
  Typography,
} from "antd";
import React, { useMemo, useState } from "react";
import ReportService from "../../services/ReportService";
import DownloadCsv from "../Report/DailyReport/DownloadCsv";
import { SearchOutlined } from "@ant-design/icons";
import ResponseMessage from "./Notification/ResponseMessge";

const { Text } = Typography;

const OutwaredBinReportTable = ({ location }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const locationMap = {
    garment_super_market: "Garment Super Market",
    dispatch_center: "Dispatch Center",
    washing: "Washing",
  };

  const columns = [
    {
      title: "SI No.",
      dataIndex: "si_no",
      key: "si_no",
      width: 90,
    },
    {
      title: "Bin ID",
      dataIndex: "bin_id",
      key: "bin_id",
      render: (text) => text || "No Data",
      width: 120,
    },
    {
      title: "From",
      dataIndex: "bin_from",
      key: "bin_from",
      render: (text) => locationMap[text] || text || "No Data",
      width: 180,
    },
    {
      title: "To",
      dataIndex: "bin_to",
      key: "bin_to",
      render: (text) => locationMap[text] || text || "No Data",
      width: 180,
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      key: "added_by",
      render: (text) => text || "No Data",
      width: 140,
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      key: "added_on",
      render: (text) => text || "No Data",
      width: 180,
    },
    {
      title: "Received On",
      dataIndex: "received_on",
      key: "received_on",
      render: (text) => text || "No Data",
      width: 180,
    },
    {
      title: "Received By",
      dataIndex: "received_by",
      key: "received_by",
      render: (text) => text || "No Data",
      width: 140,
    },
    {
      title: "Status",
      dataIndex: "movement_status",
      key: "movement_status",
      render: (text) => text || "No Data",
      width: 130,
    },
  ];

  const handleDateChange = (dateRange) => {
    if (!dateRange || !Array.isArray(dateRange)) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const [start_Date, end_Date] = dateRange;

    if (start_Date && end_Date) {
      setStartDate(start_Date.format("YYYY-MM-DD"));
      setEndDate(end_Date.format("YYYY-MM-DD"));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  const handleClear = () => {
    form.resetFields();
    setStartDate(null);
    setEndDate(null);
    setSelectedType(null);
    setData([]);
    setSearchText("");
  };

  const handleGetResult = async () => {
    if (!startDate || !endDate || !selectedType || !location) {
      ResponseMessage.error({ message: "Please provide all required inputs" });
      return;
    }

    try {
      setLoading(true);
      const response = await ReportService.get_inward_outward_report(
        startDate,
        endDate,
        selectedType,
        location
      );

      if (response && Array.isArray(response.data)) {
        const updatedData = response.data.map((item, index) => ({
          si_no: index + 1,
          ...item,
        }));

        setData(updatedData);

        if (response?.message) {
          ResponseMessage.info({ message: response.message });
        }
      } else {
        setData([]);
        ResponseMessage.error({
          message: response?.message || "No data found.",
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      ResponseMessage.error({
        message: error?.message || "Failed to fetch data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    {
      value: "inward",
      label: "Inwarded Bin",
    },
    {
      value: "outward",
      label: "Outwarded Bin",
    },
  ];

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) =>
      item?.bin_id?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [data, searchText]);

  const totalRecords = filteredData.length;

  return (
    <>
      <Card className="modern-card dashboard-filter-wrap" style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} lg={8}>
            <div className="page-title-wrap">
              <Text style={{ fontSize: 16, fontWeight: 600, color: "#1e293b" }}>
                Bin Movement Filters
              </Text>
              <Text className="page-subtitle">
                Filter inward and outward bin reports by type and date range
              </Text>
            </div>
          </Col>

          <Col xs={24} lg={16}>
            <Form layout="vertical" form={form}>
              <Row gutter={[12, 12]} align="bottom">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<strong>Type</strong>}
                    name="type"
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Select a Type"
                      options={typeOptions}
                      onChange={handleTypeChange}
                      value={selectedType}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={10}>
                  <Form.Item
                    label={<strong>Select Date Range</strong>}
                    name="dateRange"
                    style={{ marginBottom: 0 }}
                  >
                    <DatePicker.RangePicker
                      onChange={handleDateChange}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={12} sm={3}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" onClick={handleGetResult} block>
                      Get Result
                    </Button>
                  </Form.Item>
                </Col>

                <Col xs={12} sm={3}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button danger onClick={handleClear} block>
                      Clear
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Card>

      <div className="compact-stats" style={{ marginBottom: 20 }}>
        <Row gutter={[16, 8]} justify="center">
          <Col xs={12} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f172a" }}>
                {totalRecords}
              </div>
              <div className="compact-stat-label">Filtered Records</div>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f766e" }}>
                {selectedType ? selectedType.toUpperCase() : "--"}
              </div>
              <div className="compact-stat-label">Selected Type</div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div className="compact-stat-item">
              <div
                className="compact-stat-value"
                style={{ color: "#7c3aed", fontSize: 16 }}
              >
                {startDate && endDate ? `${startDate} - ${endDate}` : "--"}
              </div>
              <div className="compact-stat-label">Selected Period</div>
            </div>
          </Col>
        </Row>
      </div>

    <Card className="modern-card">
  <Table
    title={() => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          padding: "8px 4px 4px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            Bin Movement Report
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Search, review, and export inward / outward bin records
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              minWidth: 260,
              flex: 1,
            }}
          >
            <Input
              placeholder="Search Bin ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
              prefix={<SearchOutlined style={{ color: "#64748b" }} />}
              style={{
                borderRadius: 12,
                height: 42,
                boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                padding: "8px 14px",
                borderRadius: 12,
                background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                border: "1px solid #e2e8f0",
                fontSize: 13,
                fontWeight: 600,
                color: "#334155",
              }}
            >
              Records: {filteredData?.length || 0}
            </div>

            <DownloadCsv
              flag={true}
              reportData={filteredData}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>
    )}
    columns={columns}
    dataSource={filteredData}
    rowKey={(record, index) => record.bin_id || index}
    size="middle"
    loading={loading}
    scroll={{ x: 1300 }}
   pagination={{
  pageSize: 10,
  showSizeChanger: false,
  showQuickJumper: false,
  position: ["bottomRight"],
  itemRender: (page, type, originalElement) => {
    if (type === "prev") {
      return <span className="premium-page-arrow">‹</span>;
    }
    if (type === "next") {
      return <span className="premium-page-arrow">›</span>;
    }
    return originalElement;
  },
}}
    className="premium-report-table"
  />
</Card>
    </>
  );
};

export default OutwaredBinReportTable;